import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '../config/env'
import { ApiError } from '../lib/api'
import type { ProviderRecord } from '../features/providers/types'
import type { FilterCondition } from '../utils/filterUtils'

export interface ProvidersQueryParams {
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  // Structured filters serialized as filter[field][operator]=value (parsed +
  // validated server-side by relay's /data/providers).
  filters?: FilterCondition[]
}

export interface ProvidersResponse {
  data: ProviderRecord[]
  pagination: {
    limit: number
    has_more: boolean
    next_cursor: string | null
    total: number | null
  }
}

async function fetchProviders(
  token: string,
  params: ProvidersQueryParams,
  cursor?: string
): Promise<ProvidersResponse> {
  const search = new URLSearchParams()
  if (params.limit) {
    search.set('limit', params.limit.toString())
  }
  if (cursor) {
    search.set('cursor', cursor)
  }
  if (params.sort_by) {
    search.set('sort_by', params.sort_by)
  }
  if (params.sort_order) {
    search.set('sort_order', params.sort_order)
  }
  if (params.filters && params.filters.length > 0) {
    for (const filter of params.filters) {
      const value = filter.value.trim()
      if (!value) continue
      search.append(`filter[${filter.field}][${filter.operator}]`, value)
    }
  }

  const url = `${AGENT_URL}/data/providers?${search.toString()}`

  const response = await fetch(url, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch providers: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      // relay validation errors surface as `detail`; hoku's own errors as `error`.
      errorMessage = errorData.detail || errorData.error || errorMessage
    } catch {
      if (errorText) errorMessage = errorText
    }
    throw new ApiError(errorMessage, response.status)
  }

  const raw: any = await response.json()
  const rawProviders: any[] = Array.isArray(raw?.data) ? raw.data : []

  const data: ProviderRecord[] = rawProviders.map(
    (provider: any): ProviderRecord => ({
      id: String(provider.id ?? ''),
      created_at:
        typeof provider.created_at === 'string' ? provider.created_at : null,
      provider_name:
        typeof provider.provider_name === 'string'
          ? provider.provider_name
          : null,
      specialty:
        typeof provider.specialty === 'string' ? provider.specialty : null,
      credentials:
        typeof provider.credentials === 'string' ? provider.credentials : null,
    })
  )

  const rawPagination = raw?.pagination ?? {}
  const limit =
    typeof rawPagination.limit === 'number'
      ? rawPagination.limit
      : (params.limit ?? 25)

  const has_more = Boolean(rawPagination.has_more)
  const next_cursor =
    typeof rawPagination.next_cursor === 'string'
      ? (rawPagination.next_cursor as string)
      : null

  const total =
    typeof rawPagination.total === 'number' ? rawPagination.total : null

  return {
    data,
    pagination: {
      limit,
      has_more,
      next_cursor,
      total,
    },
  }
}

export function useProvidersQuery(params: ProvidersQueryParams = {}) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    queryKey: ['providers', organization?.id, params],
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchProviders(token, params, pageParam as string | undefined)
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    // Keep prior rows visible during a filtered refetch so the table doesn't
    // flash its empty ("no results") state while the request resolves.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
